import { Content } from '@phrasee/phrasee-typings'
import {
  Element,
  Variant,
} from '@phrasee/phrasee-typings/typings/futurama/element'
import { format } from 'date-fns'
import cloneDeep from 'lodash/cloneDeep'
import cond from 'lodash/cond'
import find from 'lodash/find'
import flatMap from 'lodash/flatMap'
import get from 'lodash/get'
import isNil from 'lodash/isNil'
import map from 'lodash/map'
import range from 'lodash/range'
import stubTrue from 'lodash/stubTrue'
import { SubjectLine } from 'workflow/interface'
import helpers from 'workflow/utils/helpers'

import { capitalizeFirstLetter } from 'common/helpers/string'

interface AttributeDimension {
  key: string
  value: string
}

interface MappedVariant<T extends string | number> {
  text: string
  id: T
  sortOrder: number
  parentVariantId?: T
  combinationId?: string
  attributes?: AttributeDimension[]
}

interface ExportElementData {
  name: string
  variants: MappedVariant<string | number>[]
  selectedVariants: number[]
}

const getChildVariants = (
  variants: MappedVariant<string | number>[],
  parentVariant: MappedVariant<string | number>
) =>
  variants
    ?.filter((variant) => variant.parentVariantId === parentVariant.id)
    ?.sort((a, b) => a.sortOrder - b.sortOrder)

const sortVariantsBySelections = <T extends string | number>(
  a: MappedVariant<T>,
  b: MappedVariant<T>,
  selectedVariants: T[]
) => {
  const aIsSelected = selectedVariants?.includes(a.id)
  const bIsSelected = selectedVariants?.includes(b.id)

  if (aIsSelected && !bIsSelected) {
    return -1
  } else if (!aIsSelected && bIsSelected) {
    return 1
  } else {
    return 0
  }
}

const getSortedComponentElements = (elements: ExportElementData[]) =>
  elements.map((element) => ({
    ...element,
    variants: flatMap(element.variants, (variant) =>
      isNil(variant.parentVariantId)
        ? [variant, ...getChildVariants(element?.variants, variant)]
        : []
    ).sort((a, b) => sortVariantsBySelections(a, b, element.selectedVariants)),
  }))

const getAttributeKeys = (elements: ExportElementData[]): string[] => {
  const keys = new Set<string>()

  elements.forEach((element) => {
    element.variants?.forEach((variant) => {
      variant.attributes?.forEach(({ key }) => keys.add(key))
    })
  })

  return Array.from(keys)
}

const getRows = (
  sortedComponentElements: ExportElementData[],
  clonedComponentElements: ExportElementData[],
  attributeKeys: string[]
) => {
  const maxVariantsLength = Math.max(
    ...clonedComponentElements.map((element) => element.variants?.length || 0)
  )

  return map(range(maxVariantsLength), (rowIndex) => {
    let hasSelection = false

    const rowVariants: (MappedVariant<string | number> | undefined)[] = map(
      sortedComponentElements,
      (element) => {
        const { selectedVariants, variants } = element

        const selectedVariantId = get(selectedVariants, rowIndex)
        const matchedVariant = find(variants, { id: selectedVariantId })

        return cond([
          [
            () => !!selectedVariantId && !!matchedVariant,
            () => {
              hasSelection = true
              return matchedVariant
            },
          ],
          [() => !!get(variants, rowIndex), () => get(variants, rowIndex)],
          [stubTrue, () => undefined],
        ])()
      }
    )

    const textCells = map(rowVariants, (variant) => variant?.text ?? '')

    const rowAttributes =
      find(rowVariants, (variant) => !!variant?.attributes?.length)
        ?.attributes ?? []
    const attributesByKey = new Map(
      rowAttributes.map(({ key, value }) => [key, value])
    )
    const attributeCells = attributeKeys.map(
      (key) => attributesByKey.get(key) ?? ''
    )

    const row = [...textCells, ...attributeCells]

    return hasSelection ? [...row, 'selected'] : row
  })
}

export const getFileContent = (elements: ExportElementData[]) => {
  const clonedComponentElements = cloneDeep(elements)

  const attributeKeys = getAttributeKeys(clonedComponentElements)

  const headers = [
    ...clonedComponentElements.map(({ name }) => name),
    ...attributeKeys.map((key) => capitalizeFirstLetter(key)),
  ]

  const sortedComponentElements = getSortedComponentElements(
    clonedComponentElements
  )

  const dataRows = getRows(
    sortedComponentElements,
    clonedComponentElements,
    attributeKeys
  )

  return [headers, ...dataRows]
}

export const getFileName = (element: Element, content: Content | undefined) => {
  return `${content?.display_name}_${
    element.display_name || element.name
  }_${format(new Date(Date.now()), 'MM/dd/yyyy')}`
}

export const optimizationVariantMapper = ({
  text,
  _id,
  sort_order,
}: SubjectLine) => ({
  text,
  id: _id,
  sortOrder: sort_order,
  parentVariantId: null,
})

export const contentVariantMapper = ({
  text,
  variant_id,
  sortOrder,
  parent_variant_id,
  combinationId,
}: Variant) => ({
  text,
  id: variant_id,
  sortOrder,
  parentVariantId: parent_variant_id,
  combinationId,
})

export const elementDataFormatter = ({
  name,
  element,
  variantsPath,
  variantsMapperHandler,
  statusFilter,
  combinationsMap,
}: {
  name: string
  element: unknown
  variantsPath: string
  variantsMapperHandler: Function
  statusFilter?: string
  combinationsMap?: Record<string, AttributeDimension[]>
}) => {
  let allVariants = get(element, variantsPath, [])

  // Filter variants by status if statusFilter is provided
  if (statusFilter) {
    allVariants = allVariants.filter(
      (
        variant: Variant & {
          bandit_status?: { status: string }
          variantStatus?: string
        }
      ) => {
        const status =
          variant.bandit_status?.status ||
          variant.status ||
          variant.variantStatus

        if (statusFilter === 'pending') {
          return status === 'pending'
        } else if (statusFilter === 'live') {
          return status === 'live' || status === 'approved'
        } else if (statusFilter === 'dropped') {
          return (
            status === 'dropped' ||
            status === 'rejected' ||
            status === 'replaced'
          )
        }

        return true
      }
    )
  }

  const mappedVariants: MappedVariant<string | number>[] = allVariants.map(
    (variant: Variant) => variantsMapperHandler(variant)
  )

  const variants = combinationsMap
    ? mappedVariants.map((variant) => ({
        ...variant,
        attributes: variant.combinationId
          ? (combinationsMap[variant.combinationId] ?? [])
          : [],
      }))
    : mappedVariants

  return {
    name,
    variants,
    selectedVariants: get(element, 'selected_variants', []),
  }
}

export const downloadCsvFile = (
  element: Element,
  data: ExportElementData[],
  content: Content | undefined
) => {
  helpers.downloadCsv(getFileName(element, content), getFileContent(data))
}

export const downloadXlsFile = (
  element: Element,
  data: ExportElementData[],
  content: Content | undefined
) => {
  helpers.downloadXls(getFileName(element, content), getFileContent(data))
}
